import { FC, useState, useEffect } from 'react'
import { ISectionTestMatchWordsWithImages, ISessionAnswer } from 'shared/types'
import { VStack, HStack, Grid } from '@chakra-ui/react'
import TestTask from 'shared/components/sections/tests/TestTask'
import get from 'lodash/get'
import map from 'lodash/map'
import shuffle from 'lodash/shuffle'
import last from 'lodash/last'
import has from 'lodash/has'
import find from 'lodash/find'
import omit from 'lodash/omit'
import isNil from 'lodash/isNil'
import forEach from 'lodash/forEach'
import size from 'lodash/size'
import { getTestTitle } from 'shared/utils/tests'
import TestCard from 'shared/components/sections/tests/TestCard'
import DraggableWord, {
  IMatch
} from 'shared/components/sections/sectionTestArrangeWords/DraggableWord'
import MatchingImage from 'shared/components/sections/sectionTestMatchWithImages/MatchingImage'
import MatchingImageExamination from 'shared/components/sections/sectionTestMatchWithImages/MatchingImageExamination'

type Props = {
  s: ISectionTestMatchWordsWithImages
  onAnswer?: (sectionId: string, answer: ISessionAnswer) => void
  answer?: ISessionAnswer
  testNum?: number
  showUserAnswer?: boolean
  isExamination: boolean
}

const SectionTestMatchWithImages: FC<Props> = ({
  s,
  onAnswer,
  answer: answerRaw,
  testNum,
  showUserAnswer,
  isExamination
}) => {
  const [matches, setMatches] = useState<IMatch[]>([])
  const [started, setStarted] = useState(false)

  useEffect(() => {
    const rawMatches: IMatch[] = map(s.test?.matching, (m) => {
      return { id: m.id, value: m.word }
    })
    setMatches(shuffle(rawMatches))
  }, [s.test?.matching])

  useEffect(() => {
    if (matches.length > 0 && started) {
      let answersAmount = 0
      let correctAmount = 0
      forEach(matches, (t) => {
        if (has(t, 'correct')) {
          answersAmount += 1
        }

        if (t.correct === true) {
          correctAmount += 1
        }
      })

      const sa: ISessionAnswer = {
        isCorrect:
          matches.length === answersAmount && answersAmount === correctAmount,
        answer: '',
        progress: {
          correctAmount,
          fullAmount: matches.length,
          completed: matches.length === answersAmount,
          answersAmount: answersAmount
        }
      }
      if (onAnswer) {
        onAnswer(s.id, sa)
      }
    }
  }, [matches, started])

  const answer =
    answerRaw && get(answerRaw.progress, 'completed', false)
      ? answerRaw
      : undefined

  const onDragEndResult = (matchId: string, attempts: boolean[]) => {
    console.log('onDragEndResult called with:', { matchId, attempts })
    const isCompleted = last(attempts) === true || attempts.length === 3
    console.log('isCompleted:', isCompleted)
    if (isCompleted) {
      setMatches((matches) => {
        const newMatches = map(matches, (m) => {
          if (!has(m, 'correct') && m.id === matchId) {
            console.log('Updating match:', m)
            return { ...m, correct: last(attempts) }
          } else {
            return m
          }
        })
        console.log('newMatches:', newMatches)
        return newMatches
      })
      setStarted(true)
    }
  }

  const onDragEndResultExamination = (
    value: string,
    droppedValue: string,
    isCorrect: boolean
  ) => {
    console.log('onDragEndResultExamination called with:', {
      value,
      droppedValue,
      isCorrect
    })
    setMatches((matches) => {
      let added = false
      const newMatches = map(matches, (m) => {
        console.log('onDragEndResultExamination', {
          m,
          value,
          droppedValue,
          isCorrect
        })
        if (m.id === value) {
          if (!added) {
            console.log('Updating match:', m)
            added = true
            return { ...m, correct: isCorrect, droppedValue }
          } else {
            return m
          }
        } else if (m.droppedValue === droppedValue) {
          return omit(m, ['correct', 'droppedValue'])
        } else {
          return m
        }
      })
      console.log('newMatches:', newMatches)
      return newMatches
    })
    setStarted(true)
  }

  const renderTaskWords = () => {
    return (
      <HStack w='full' wrap={'wrap'}>
        {map(matches, (t, i) => {
          return (
            <DraggableWord
              t={t}
              key={i}
              isComplete={
                isExamination ? !showUserAnswer : !isNil(answer?.answer)
              }
              isExamination={isExamination}
            />
          )
        })}
      </HStack>
    )
  }

  const renderImages = () => {
    const imagesAmount = size(s.test.matching)
    // Calculate imagesPerRow to use space effectively with max 8 images and 2 rows
    let imagesPerRow = 4
    if (imagesAmount <= 4) {
      imagesPerRow = imagesAmount // All images in one row
    } else if (imagesAmount <= 8) {
      imagesPerRow = Math.ceil(imagesAmount / 2) // Two rows, as even as possible
    }

    return (
      <Grid
        templateColumns={{
          base: 'repeat(2, 1fr)',
          lg: `repeat(${imagesPerRow}, 1fr)`
        }}
        // gap={4}
        rowGap={{ base: 4, lg: 8 }}
        columnGap={{ base: 3, lg: 4 }}
        w='full'
      >
        {map(s.test.matching, (m, i) => {
          const iMatch = find(matches, (match) => match.id === m.id)
          if (iMatch) {
            if (isExamination) {
              const droppedWord = get(
                find(matches, (match) => match.id === iMatch.droppedValue),
                'value'
              )
              return (
                <MatchingImageExamination
                  key={m.id}
                  m={m}
                  onDragEndResult={onDragEndResultExamination}
                  isComplete={!showUserAnswer}
                  droppedValue={iMatch.droppedValue}
                  droppedWord={droppedWord}
                />
              )
            } else {
              return (
                <MatchingImage
                  key={m.id}
                  m={m}
                  onDragEndResult={onDragEndResult}
                />
              )
            }
          }
        })}
      </Grid>
    )
  }

  const renderQuestion = () => {
    if (s.test) {
      return (
        <VStack
          justify={'flex-start'}
          align={'flex-start'}
          spacing={{ lg: 12, base: 8 }}
          pt={{ base: 4, lg: 8 }}
          w='full'
        >
          {renderTaskWords()}
          {renderImages()}
        </VStack>
      )
    }
  }

  return (
    <TestCard showUserAnswer={showUserAnswer} answer={answer}>
      <TestTask
        title={s.test?.title || getTestTitle(s.type)}
        answer={showUserAnswer ? undefined : answer}
        testNum={testNum}
      />
      {renderQuestion()}
    </TestCard>
  )
}

export default SectionTestMatchWithImages
